import {Alert, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import useCallDataApi from "../../hooks/data";
import {determineAddress, orderStatusMap} from "../../utils/utils";
import DetailPanel from "../../components/DetailPanel";
import {thousandSeparator} from "../../utils/NumberUtils";
import HighlightPanel from "../../components/HighlightPanel";
import Loading from "../../components/Loading";
import {useConfig} from "../../context/ConfigContext";
import {orderStatuses} from "../../utils/enums";
import useDataModel from "../../hooks/dataModel";

const OrderDetail = ({orderId}) => {
    const {fetchModels: fetchReceivers, findModel: findReceiver} = useDataModel(useCallDataApi('receivers'))
    const {fetchModels: fetchTransmitters, findModel: findTransmitter} = useDataModel(useCallDataApi('transmitters'))
    const {getData: fetchOrder} = useCallDataApi('orders')
    const {postData: postTourOffice} = useCallDataApi('tour-offices')
    const {postData: postTourGuide} = useCallDataApi('tour-guides')
    const [loading, setLoading] = useState(false)
    const [order, setOrder] = useState({})
    const [tourOffice, setTourOffice] = useState({})
    const [tourGuide, setTourGuide] = useState({})
    const {
        generalColor,
        deliveryColor,
        deviceColor,
        returnColor,
        financialColor
    } = useConfig()

    useEffect(() => {
        setLoading(true)
        fetchReceivers()
        fetchTransmitters()
        Promise.all([
            fetchOrder(orderId),
            postTourOffice('tour_office_for_order', {order_id: orderId}),
            postTourGuide('tour_guide_for_order', {order_id: orderId})
        ]).then(([o, to, tg]) => {
            setOrder(o)
            setTourOffice(to)
            setTourGuide(tg)
        }).finally(() => setLoading(false))
    }, [])

    const generalDetail = [{
        name: 'Bérlési idő',
        value: `${order?.days} nap (${order?.delivery_date} - ${order?.return_date})`,
    }, {
        name: 'Számított ár',
        value: `${thousandSeparator(Math.round(order?.price)) || '-'} ${order?.currency}`
    }, {
        name: 'Megadott ár',
        value: `${thousandSeparator(order?.agreed_price) || '-'} ${order?.currency}`
    }, {
        name: 'Megjegyzés',
        value: order?.comment,
    }, {
        name: 'Belsős megjegyzés',
        value: order?.internal_comment
    }, {
        name: 'Rendelés szám',
        value: order?.order_number
    }, {
        name: 'Lezárva',
        value: order?.closed ? 'igen' : 'nem',
    }, {
        name: 'Sürgős rendelés',
        value: order?.is_urgent_order ? 'igen' : 'nem',
    }, {
        name: 'Státusz',
        value: orderStatusMap[order?.status],
    }, {
        name: 'Megrandelő',
        value: tourOffice?.name,
    }, {
        name: 'Célország',
        value: order?.usage_country
    }, {
        name: 'Csoportkísérő',
        value: tourGuide?.show_name
    }];
    const deliveryDetail = [{
        name: 'Szállítás típusa',
        value: order?.delivery_type
    }, {
        name: 'Kiszállítás',
        value: `${order?.actual_delivery_date} ${order?.delivery_time_set ? order?.return_time : order?.delivery_period}`
    }, {
        name: 'Kiszállítási cím',
        value: determineAddress(order?.delivery_address)
    }, {
        name: 'Csomagolás',
        value: order?.delivery_format
    }, {
        name: 'Oda fuvarlevélszám',
        value: order?.DHL_delivery_number
    }, {
        name: 'Oda felvételazonosító',
        value: order?.DHL_order
    }, {
        name: 'Kiszállítás komment',
        value: order?.delivery_comment
    }];
    const deviceDetail = [{
        name: 'Vevő',
        value: findReceiver(order?.receiver)?.name
    }, {
        name: 'Adó',
        value: findTransmitter(order?.transmitter)?.name
    }, {
        name: 'Rendelt vevők száma',
        value: order?.order_rx
    }, {
        name: 'Rendelt adók száma',
        value: order?.order_tx
    }, {
        name: 'Elküldött vevők száma',
        value: order?.sent_rx
    }, {
        name: 'Elküldött adók száma',
        value: order?.sent_tx
    }];
    const returnDetail = [{
        name: 'Visszaszállítás típusa',
        value: order?.return_type
    }, {
        name: 'Visszaszállítás',
        value: `${order?.actual_return_date} ${order?.return_time_set ? order?.return_time : order?.return_period}`
    }, {
        name: 'Visszaküldött vevők száma',
        value: order?.returned_rx
    }, {
        name: 'Visszavételi cím',
        value: determineAddress(order?.return_address)
    }, {
        name: 'Vissza fuvarlevélszám',
        value: order?.DHL_return_number
    }, {
        name: 'Vissza felvételazonosító',
        value: order?.DHL_order_back
    }, {
        name: 'Visszaszállítás komment',
        value: order?.return_comment
    }];
    const financeDetail = [{
        name: 'Pénznem',
        value: order?.currency
    }, {
        name: 'Fizetési mód',
        value: order?.invoice
    }, {
        name: 'Számlasorszám',
        value: order?.invoice_number
    }];

    return <>
        {order?.is_urgent_order && <Alert severity="error" variant="filled" sx={{marginBottom: 2}}>
            Sürgős rendelés!
        </Alert>}

        <HighlightPanel color={generalColor}>
            <Typography gutterBottom variant="h5" component="div">
                Általános infó
            </Typography>
            <DetailPanel details={generalDetail}/>
        </HighlightPanel>

        <HighlightPanel color={deliveryColor}>
            <Typography gutterBottom variant="h5" component="div">
                Szállítási infó
            </Typography>
            <DetailPanel details={deliveryDetail}/>
        </HighlightPanel>

        <HighlightPanel color={deviceColor}>
            <Typography gutterBottom variant="h5" component="div">
                Eszközök
            </Typography>
            <DetailPanel details={deviceDetail}/>
        </HighlightPanel>

        <HighlightPanel color={returnColor}>
            <Typography gutterBottom variant="h5" component="div">
                Visszaszállítás adatai
            </Typography>
            <DetailPanel details={returnDetail}/>
        </HighlightPanel>

        <HighlightPanel color={financialColor}>
            <Typography gutterBottom variant="h5" component="div">
                Pénzügyi adatok
            </Typography>
            <DetailPanel details={financeDetail}/>
        </HighlightPanel>

        <Loading isLoading={loading}/>
    </>
}

export default OrderDetail